import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Plus, Trash2, Sticker, ShieldCheck, Info, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const makeDesign = (i) => ({
  id: Math.random().toString(36).slice(2, 10),
  name: `Design ${i}`,
  quantity: 10,
  notes: "",
  image_data_url: null,
});

export default function StickerBuilder() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [designs, setDesigns] = useState([makeDesign(1), makeDesign(2)]);
  const [form, setForm] = useState({
    customer_name: "", email: "", phone: "", shipping_address: "",
    military_discount: false,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, email: f.email || user.email, customer_name: f.customer_name || user.name || "" }));
  }, [user]);

  const totalQty = designs.reduce((s, d) => s + (parseInt(d.quantity) || 0), 0);

  const addDesign = () => setDesigns((ds) => [...ds, makeDesign(ds.length + 1)]);
  const removeDesign = (id) => setDesigns((ds) => ds.length > 1 ? ds.filter((d) => d.id !== id) : ds);
  const updateDesign = (id, patch) => setDesigns((ds) => ds.map((d) => d.id === id ? { ...d, ...patch } : d));

  const onFile = (id, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Keep uploads under 5MB"); return; }
    const r = new FileReader();
    r.onload = () => updateDesign(id, { image_data_url: r.result });
    r.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Please sign in to continue"); nav("/login"); return; }
    if (designs.length === 0) { toast.error("Add at least one design"); return; }
    setSubmitting(true);
    try {
      const summary = designs.map((d, i) => `${i + 1}. ${d.name} — ${d.quantity} stickers${d.notes ? ` (${d.notes})` : ""}`).join("\n");
      const payload = {
        customer_name: form.customer_name,
        email: form.email,
        phone: form.phone,
        shipping_address: form.shipping_address,
        material: "Vinyl — mixed designs",
        description: `Mixed sticker order — ${designs.length} design(s), ${totalQty} stickers total.\n\n${summary}`,
        quantity: totalQty,
        tier: "custom-quote",
        rush: false,
        military_discount: form.military_discount,
        complexity: "mixed-stickers",
        desired_deadline: "",
        design_data: { kind: "mixed-stickers", designs },
      };
      const { data: order } = await api.post("/orders", payload);
      toast.success("Mix submitted — redirecting…");
      nav(`/quote-received/${order.order_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit mix");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="sticker-builder-page" className="max-w-5xl mx-auto px-6 lg:px-10 py-16">
      <div className="uppercase text-xs tracking-[0.3em] font-bold" style={{color: "var(--secondary)"}}>Multi-Design Sticker Order</div>
      <h1 className="font-serif text-5xl sm:text-6xl mt-3 leading-none" style={{color: "var(--primary)"}}>Build your mix.</h1>
      <p className="mt-4 max-w-2xl text-[color:var(--text-muted)]">
        Order a variety of designs on one invoice. Add as many designs as you want and set the quantity for each — we'll review the mix and quote you within 24 hours.
      </p>

      {/* INFO BANNER */}
      <div data-testid="pricing-note" className="mt-6 rounded-lg border hairline p-4 flex items-start gap-3" style={{background: "rgba(197,160,89,0.08)"}}>
        <Info size={18} strokeWidth={1.75} style={{color: "var(--accent-metal)"}} className="mt-0.5 shrink-0"/>
        <div className="text-sm text-[color:var(--text-muted)] leading-relaxed">
          Pack pricing ($20 / $55 / $85) is for <span className="font-semibold" style={{color:"var(--primary)"}}>one design</span>. Multi-design orders are quoted individually — set-up and sheet layout change the cost. The more each design repeats, the lower the per-sticker price.
        </div>
      </div>

      {/* DESIGN ROWS */}
      <div className="mt-10 space-y-5">
        {designs.map((d, i) => (
          <div key={d.id} data-testid={`design-row-${i}`} className="bg-white rounded-lg border hairline p-6">
            <div className="flex items-center justify-between gap-4 mb-5">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full flex items-center justify-center font-serif text-lg" style={{background: "rgba(197,160,89,0.14)", color: "var(--accent-metal)"}}>{i + 1}</div>
                <div className="uppercase text-xs tracking-[0.3em] font-bold" style={{color: "var(--secondary)"}}>Design {i + 1}</div>
              </div>
              <button type="button" data-testid={`remove-design-${i}`} onClick={() => removeDesign(d.id)} disabled={designs.length === 1}
                className="text-sm text-[color:var(--text-muted)] hover:text-red-700 flex items-center gap-1 disabled:opacity-30 disabled:cursor-not-allowed">
                <Trash2 size={14}/> Remove
              </button>
            </div>
            <div className="grid md:grid-cols-[2fr_1fr] gap-5">
              <div>
                <Label>Design name / label</Label>
                <Input data-testid={`design-name-${i}`} required value={d.name} onChange={(e) => updateDesign(d.id, { name: e.target.value })} placeholder="e.g. 'Mountain Logo'"/>
              </div>
              <div>
                <Label>Quantity</Label>
                <Input data-testid={`design-qty-${i}`} type="number" min={1} required value={d.quantity} onChange={(e) => updateDesign(d.id, { quantity: parseInt(e.target.value) || 1 })}/>
              </div>
            </div>
            <div className="mt-5">
              <Label>Notes — size, colors, finish, references</Label>
              <Textarea data-testid={`design-notes-${i}`} rows={2} value={d.notes} onChange={(e) => updateDesign(d.id, { notes: e.target.value })} placeholder="e.g. '3in holographic, matte finish — see uploaded artwork'"/>
            </div>
            <div className="mt-5">
              <Label>Artwork (optional)</Label>
              <div className="mt-2 flex items-center gap-4">
                <label data-testid={`design-upload-${i}`} className="inline-flex items-center gap-2 cursor-pointer px-4 py-2 rounded-full border hairline text-sm hover:bg-[color:var(--muted)]/50 transition-colors">
                  <ImagePlus size={14} strokeWidth={1.75}/>
                  <span>{d.image_data_url ? "Replace artwork" : "Upload artwork"}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => onFile(d.id, e.target.files?.[0])}/>
                </label>
                {d.image_data_url && (
                  <img src={d.image_data_url} alt={d.name} className="h-14 w-14 object-cover rounded border hairline"/>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <button type="button" data-testid="add-design" onClick={addDesign}
        className="mt-5 w-full rounded-lg border-2 border-dashed hairline p-5 text-sm font-medium text-[color:var(--text-muted)] hover:border-[color:var(--accent-metal)] hover:text-[color:var(--primary)] transition-colors flex items-center justify-center gap-2">
        <Plus size={16} strokeWidth={2}/> Add another design
      </button>

      {/* SUMMARY + SHIPPING */}
      <form onSubmit={submit} data-testid="mix-form" className="mt-10 bg-white rounded-lg border hairline p-8 space-y-6">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <div className="font-serif text-2xl" style={{color: "var(--primary)"}}>Order summary</div>
          <div className="flex items-center gap-2 text-sm text-[color:var(--text-muted)]">
            <Sticker size={14} strokeWidth={1.75} style={{color: "var(--accent-metal)"}}/>
            <span data-testid="total-count"><span className="font-serif text-xl mr-1" style={{color: "var(--primary)"}}>{totalQty}</span>stickers across <span className="font-semibold">{designs.length}</span> design{designs.length > 1 ? "s" : ""}</span>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label>Full name</Label>
            <Input data-testid="mix-name" required value={form.customer_name} onChange={(e) => setForm({...form, customer_name: e.target.value})}/>
          </div>
          <div>
            <Label>Email</Label>
            <Input data-testid="mix-email" type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}/>
          </div>
          <div>
            <Label>Phone</Label>
            <Input data-testid="mix-phone" required value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}/>
          </div>
          <div>
            <Label>Shipping address</Label>
            <Input data-testid="mix-address" required value={form.shipping_address} onChange={(e) => setForm({...form, shipping_address: e.target.value})}/>
          </div>
        </div>

        {/* MILITARY */}
        <div data-testid="mix-military" className="rounded-lg border hairline p-5 flex items-start gap-4" style={{background: form.military_discount ? "rgba(197,160,89,0.10)" : "#F3F1EC"}}>
          <button
            type="button"
            data-testid="mix-military-toggle"
            role="switch"
            aria-checked={form.military_discount}
            onClick={() => setForm({...form, military_discount: !form.military_discount})}
            className={`relative shrink-0 mt-1 w-12 h-7 rounded-full transition-colors ${form.military_discount ? "bg-[color:var(--accent-metal)]" : "bg-neutral-300"}`}
          >
            <span aria-hidden className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow-md ring-1 ring-black/5 transition-transform duration-200 ${form.military_discount ? "translate-x-5" : "translate-x-0"}`}/>
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} strokeWidth={2} style={{color: "var(--accent-metal)"}}/>
              <span className="font-serif text-lg" style={{color: "var(--primary)"}}>Military & First Responder — 10% off the final quote</span>
            </div>
            <p className="mt-1 text-xs text-[color:var(--text-muted)] leading-relaxed">
              Active duty, veteran, fire/EMS/police, and <span className="font-semibold">immediate family members</span> qualify. We'll request simple proof after submission.
            </p>
          </div>
        </div>

        <div className="pt-2 flex flex-wrap gap-3 justify-end items-center">
          <div className="text-xs text-[color:var(--text-muted)] mr-auto">No payment taken now — we'll quote within 24h.</div>
          <Button data-testid="submit-mix" type="submit" disabled={submitting || totalQty < 1} className="btn-primary rounded-full px-8 py-6">
            {submitting ? "Submitting…" : "Request a quote for my mix"}
          </Button>
        </div>
      </form>
    </div>
  );
}
